// src/modules/config/Config.tsx - ПОЛНАЯ ОБНОВЛЕННАЯ ВЕРСИЯ с исправленными фильтрами
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Tabs,
  Button,
  Space,
  Row,
  Col,
  Typography,
  Badge,
  message,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  AppstoreOutlined,
  ShoppingOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useConfig } from '@/hooks/useConfig';
import { MenuItem, MenuCategory } from '@/types';

// Импорты новых компонентов
import { ItemsTable } from './components/tables/ItemsTable';
import { CategoriesTable } from './components/tables/CategoriesTable';
import { ItemsFilters } from './components/filters/ItemsFilters';
import { QRStatsPanel } from './components/panels/QRStatsPanel';
import { BulkActions } from './components/panels/BulkActions';

// Импорты модальных окон
import { CategoryModal } from './components/CategoryModal';
import { MenuItemModal } from './components/MenuItemModal';
import { DiscountModal } from './components/DiscountModal';

const { Title } = Typography;

interface AxiosError {
  response?: {
    data?: {
      message?: string;
      errors?: Record<string, string | string[]>;
    };
  };
  message: string;
}

export const Config: React.FC = () => {
  const {
    categories,
    menuItems,
    qrStats,
    pagination,
    loading,
    categoriesLoading,
    itemsLoading,
    siteConfig,
    loadCategories,
    loadMenuItems,
    loadQrStats,
    createCategory,
    updateCategory,
    deleteCategory,
    createMenuItem,
    updateMenuItem,
    updateMenuItemData,
    deleteMenuItem,
    createDiscount,
    removeDiscount,
    generateQrCode,
    removeQrCode,
    bulkGenerateQrCodes,
    bulkDownloadQrCodes, // !!! Получаем новый метод
    getConfigStats    
  } = useConfig();

  // State для модальных окон
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [discountModalVisible, setDiscountModalVisible] = useState(false);
  
  const [selectedCategory, setSelectedCategory] = useState<MenuCategory | null>(null);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // State для фильтров
  const [filters, setFilters] = useState({
    categoryIds: [] as number[],
    status: '' as string,
    search: '' as string,
    hasDiscount: undefined as boolean | undefined,
    hasQrCode: undefined as boolean | undefined
  });

  const [catalogStats, setCatalogStats] = useState<any>(null);

  // НОВОЕ: Ref для дебаунса поиска
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      const stats = await getConfigStats();
      setCatalogStats(stats);
    };
    loadStats();
  }, []);

  // ИСПРАВЛЕННЫЕ обработчики для фильтров
  const handleFilterChange = (key: string, value: any) => {
    console.log(`Filter change: ${key} = `, value);
    
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // ИСПРАВЛЕНИЕ: Для поиска применяем дебаунс
    if (key === 'search') {
      // Очищаем предыдущий таймаут
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Устанавливаем новый таймаут для поиска
      searchTimeoutRef.current = setTimeout(() => {
        applyFilters(newFilters);
      }, 500); // 500ms дебаунс для поиска
      
      return; // Выходим, не применяя фильтр сразу
    }
    
    // Для остальных фильтров применяем сразу
    applyFilters(newFilters);
  };

  // НОВАЯ функция для применения фильтров
  const applyFilters = (currentFilters: typeof filters) => {
    console.log('Applying filters:', currentFilters);
    
    const params: any = {};
    
    // Фильтр по категориям
    if (currentFilters.categoryIds.length > 0) {
      params.category_ids = currentFilters.categoryIds;
      console.log('Category filter applied:', params.category_ids);
    }
    
    // Фильтр по статусу
    if (currentFilters.status) {
      params.status = currentFilters.status;
      console.log('Status filter applied:', params.status);
    }
    
    // Фильтр по поиску
    if (currentFilters.search && currentFilters.search.trim()) {
      params.search = currentFilters.search.trim();
      console.log('Search filter applied:', params.search);
    }
    
    console.log('Final API params:', params);
    
    // Вызываем API с фильтрами
    loadMenuItems(params);
  };

  const clearAllFilters = () => {
    console.log('Clearing all filters');
    
    // Очищаем таймаут поиска
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    const clearedFilters = {
      categoryIds: [],
      status: '',
      search: '',
      hasDiscount: undefined,
      hasQrCode: undefined
    };
    
    setFilters(clearedFilters);
    
    // Загружаем все данные без фильтров
    loadMenuItems();
  };

  // ========== ДОБАВЬТЕ ЗДЕСЬ ==========
  const handlePaginationChange = (page: number, pageSize: number) => {
    console.log(`Загрузка страницы ${page} с размером ${pageSize}`);
    
    const params: any = {
      page,
      per_page: pageSize
    };
    
    // Применяем текущие фильтры
    if (filters.categoryIds.length > 0) {
      params.category_ids = filters.categoryIds;
    }
    if (filters.status) {
      params.status = filters.status;
    }
    if (filters.search && filters.search.trim()) {
      params.search = filters.search.trim();
    }
    
    loadMenuItems(params);
  };

  // Очистка таймаута при размонтировании компонента
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // ========== ОБРАБОТЧИКИ ДЛЯ КАТЕГОРИЙ ==========
  const handleCreateCategory = () => {
    setSelectedCategory(null);
    setCategoryModalVisible(true);
  };

  const handleEditCategory = (category: MenuCategory) => {
    setSelectedCategory(category);
    setCategoryModalVisible(true);
  };

  const handleCategorySubmit = async (values: any) => {
    if (selectedCategory) {
      await updateCategory(selectedCategory.id, values);
    } else {
      await createCategory(values);
    }
    setCategoryModalVisible(false);
    setSelectedCategory(null);
  };

  const handleCategoryCancel = () => {
    setCategoryModalVisible(false);
    setSelectedCategory(null);
  };

  // ========== ОБРАБОТЧИКИ ДЛЯ БЛЮД ==========
  const handleCreateItem = () => {
    setSelectedItem(null);
    setItemModalVisible(true);
  };

  const handleEditItem = (item: MenuItem) => {
    setSelectedItem(item);
    setItemModalVisible(true);
  };

  const handleItemSubmit = async (values: any) => {
    try {
      // ИСПРАВЛЕНИЕ: Преобразуем категории в массив ID
      const submitData = {
        ...values,
        category: Array.isArray(values.category) 
          ? values.category.map((cat: any) => typeof cat === 'object' ? cat.id : cat)
          : values.category
      };
      
      console.log('Sending data:', submitData); // Для отладки
      
      if (selectedItem) {
        // При редактировании проверяем есть ли новое изображение
        const hasNewImage = values.image instanceof File;
        const hasImageOperations = 
          hasNewImage || 
          (values.additional_images && values.additional_images.length > 0) ||
          (values.images_to_remove && values.images_to_remove.length > 0);
      
        if (hasImageOperations) {
          // Используем FormData для операций с изображениями
          await updateMenuItem(selectedItem.id, submitData);
        } else {
          // Используем JSON для обычных изменений
          const jsonData = {
            ...submitData,
            category: Array.isArray(submitData.category) ? submitData.category : [submitData.category]
          };
          await updateMenuItemData(selectedItem.id, jsonData);
        }
      } else {
        // Создание нового блюда - всегда через FormData для правильной аутентификации
        const formData = new FormData();
        
        Object.entries(submitData).forEach(([key, value]) => {
          if (key === 'category' && Array.isArray(value)) {
            value.forEach((catId: number) => {
              formData.append('category', catId.toString());
            });
          } else if (value !== null && value !== undefined && !(value instanceof File)) {
            formData.append(key, value.toString());
          }
        });
        
        // Добавляем изображение если есть
        if (values.image instanceof File) {
          formData.append('image', values.image);
        }
        
        console.log('Creating with FormData');
        await createMenuItem(formData);
      }
      
      setItemModalVisible(false);
      setSelectedItem(null);
    } catch (err: unknown) {
      const error = err as AxiosError;
      console.error('Ошибка при отправке данных:', error);
      
      // Показываем детальные ошибки валидации
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        console.log('Validation errors:', errors);
        const errorMessages = Object.entries(errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('\n');
        message.error(`Ошибки валидации:\n${errorMessages}`);
      } else {
        message.error('Ошибка при сохранении блюда');
      }
    }
  };

  const handleItemCancel = () => {
    setItemModalVisible(false);
    setSelectedItem(null);
  };

  const handleDeleteItem = (itemId: number) => {
    deleteMenuItem(itemId);
  };

  // ========== ОБРАБОТЧИКИ ДЛЯ СКИДОК ==========
  const handleCreateDiscount = (item: MenuItem) => {
    setSelectedItem(item);
    setDiscountModalVisible(true);
  };

  const handleDiscountSubmit = async (discountPercent: number) => {
    if (selectedItem) {
      await createDiscount(selectedItem.id, discountPercent);
    }
    setDiscountModalVisible(false);
    setSelectedItem(null);
  };

  const handleDiscountCancel = () => {
    setDiscountModalVisible(false);
    setSelectedItem(null);
  };

  const handleRemoveDiscount = async (itemId: number) => {
    await removeDiscount(itemId);
  };

  // ========== ОБРАБОТЧИКИ ДЛЯ QR-КОДОВ ==========
  const handleGenerateQR = async (itemId: number) => {
    await generateQrCode(itemId);
  };

  const handleRemoveQR = async (itemId: number) => {
    await removeQrCode(itemId);
  };

  const handleDownloadQR = (item: MenuItem) => {
    if (item.qr_code_url) {
      const link = document.createElement('a');
      link.href = item.qr_code_url;
      link.download = `qr_${item.slug}.png`;
      link.click();
    }
  };

  const handleBulkGenerateQR = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Выберите блюда для генерации QR-кодов');
      return;
    }

    const itemIds = selectedRowKeys.map(key => Number(key));
    await bulkGenerateQrCodes(itemIds);
    setSelectedRowKeys([]);
  };

  const handleBulkDownloadQR = useCallback(async () => {
    try {
      // selectedRowKeys содержит ID выбранных блюд
      await bulkDownloadQrCodes(selectedRowKeys as number[]); 
      setSelectedRowKeys([]); // Очистка выбора после успешного скачивания
    } catch (e) {
      // Ошибка уже обработана в useConfig
    }
  }, [selectedRowKeys, bulkDownloadQrCodes]);


  // ========== RENDER ТАБОВ ==========
  const tabItems = [
    {
      key: 'items',
      label: (
        <span>
          <ShoppingOutlined />
          Блюда
          <Badge count={pagination?.total || menuItems.length} style={{ marginLeft: 8 }} />
        </span>
      ),
      children: (
        <Card
          title="Управление блюдами"
          extra={
            <Space wrap>
              <BulkActions
                selectedRowKeys={selectedRowKeys}
                loading={loading}
                onBulkGenerateQR={handleBulkGenerateQR}
                onBulkDownloadQR={handleBulkDownloadQR} // !!! Передаем новый обработчик
                onClearSelection={() => setSelectedRowKeys([])}
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  loadMenuItems();
                  loadQrStats();
                }}
                loading={itemsLoading}
                size="small"
              >
                Обновить
              </Button>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={handleCreateItem}
                size="small"
              >
                Добавить блюдо
              </Button>
            </Space>
          }
        >
          <QRStatsPanel qrStats={qrStats} />
          
          <ItemsFilters
            categories={categories}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={clearAllFilters}
          />
          
          <ItemsTable
            items={menuItems}
            categories={categories}
            loading={itemsLoading}
            pagination={pagination}
            selectedRowKeys={selectedRowKeys}
            onSelectionChange={setSelectedRowKeys}
            onEdit={handleEditItem}
            onDelete={handleDeleteItem}
            onCreateDiscount={handleCreateDiscount}
            onRemoveDiscount={handleRemoveDiscount}
            onGenerateQR={handleGenerateQR}
            onRemoveQR={handleRemoveQR}
            onDownloadQR={handleDownloadQR}
            onPaginationChange={handlePaginationChange}
          />
        </Card>
      )
    },
    {
      key: 'categories',
      label: (
        <span>
          <AppstoreOutlined />
          Категории
          <Badge count={categories.length} style={{ marginLeft: 8 }} />
        </span>
      ),
      children: (
        <Card
          title="Управление категориями"
          extra={
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadCategories}
                loading={categoriesLoading}
                size="small"
              >
                Обновить
              </Button>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={handleCreateCategory}
                size="small"
              >
                Добавить категорию
              </Button>
            </Space>
          }
        >
          {/* Статистика категорий */}
          {catalogStats && (
            <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6} md={6} lg={6}>
                <Card size="small">
                  <Statistic
                    title="Всего категорий"
                    value={catalogStats.categories.total}
                    valueStyle={{ color: '#1890ff', fontSize: '16px' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6} md={6} lg={6}>
                <Card size="small">
                  <Statistic
                    title="Активных"
                    value={catalogStats.categories.active}
                    valueStyle={{ color: '#52c41a', fontSize: '16px' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6} md={6} lg={6}>
                <Card size="small">
                  <Statistic
                    title="Неактивных"
                    value={catalogStats.categories.inactive}
                    valueStyle={{ color: '#fa8c16', fontSize: '16px' }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6} md={6} lg={6}>
                <Card size="small">
                  <Statistic
                    title="Всего блюд"
                    value={catalogStats.items.total}
                    valueStyle={{ color: '#722ed1', fontSize: '16px' }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          <CategoriesTable
            categories={categories}
            loading={categoriesLoading}
            onEdit={handleEditCategory}
            onDelete={deleteCategory}
          />
        </Card>
      )
    }
  ];

  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col xs={24} sm={16} md={18} lg={20}>
          <Title level={2} style={{ margin: 0, fontSize: '20px' }}>
            Управление каталогом
          </Title>
        </Col>
        <Col xs={24} sm={8} md={6} lg={4}>
          <Button 
            icon={<ReloadOutlined />}
            onClick={() => {
              loadCategories();
              loadMenuItems();
              loadQrStats();
            }}
            loading={loading || categoriesLoading || itemsLoading}
            block
            size="small"
          >
            Обновить все
          </Button>
        </Col>
      </Row>
      
      <Tabs 
        defaultActiveKey="items"
        items={tabItems}
        size="small"
      />

      {/* Модальные окна */}
      <CategoryModal
        visible={categoryModalVisible}
        category={selectedCategory}
        categories={categories}
        loading={loading}
        onSubmit={handleCategorySubmit}
        onCancel={handleCategoryCancel}
      />

      <MenuItemModal
        visible={itemModalVisible}
        menuItem={selectedItem}
        categories={categories}
        loading={loading}
        siteBaseUrl={siteConfig?.base_url}
        onSubmit={handleItemSubmit}
        onCancel={handleItemCancel}
        onGenerateQR={handleGenerateQR}
        onRemoveQR={handleRemoveQR}
      />

      <DiscountModal
        visible={discountModalVisible}
        menuItem={selectedItem}
        loading={loading}
        onSubmit={handleDiscountSubmit}
        onCancel={handleDiscountCancel}
      />
    </div>
  );
};